import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BookHeart,
  BowArrow,
  Camera, 
  Crown, 
  DollarSign,
  Image,
  MessageCircle, 
  Sparkles, 
  Mail,
  ExternalLink,
  Settings,
  Gem,
  Trophy,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  // SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from './ui/sidebar';
import { useUserInfo } from '@/hooks/useUserInfo';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { EH_HEADER_HEIGHT } from './EhHeader';
import EhAuthDialog from './EhAuthDialog';
import { cn } from '@/lib/utils';
import { isFromAppStores, isHidingMonetizationInStores, isTwaSession } from '@/lib/detection';

export function EhSidebar() {
  const location = useLocation();
  const { chats, isPastUser, isRenewableSubscriber, isSubscriber, user } = useUserInfo();
  
  const [contactOpen, setContactOpen] = useState(false);
  const [affiliateOpen, setAffiliateOpen] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const contentItems = [
    {
      title: 'Explore characters',
      url: '/',
      icon: BowArrow,
      show: true,
    },
    {
      title: 'Chats',
      url: '/c',
      icon: MessageCircle,
      show: !!user,
    },
    {
      title: 'Create a Character',
      url: '/create-character',
      icon: Sparkles,
      show: true,
    },
    {
      title: 'My Contacts',
      url: '/contacts',
      icon: BookHeart,
      show: true,
    },
    {
      title: 'Photo & Video Studio ⭐ NEW',
      url: '/studio',
      icon: Camera,
      show: true,
    },
    {
      title: 'Gallery ⭐ NEW',
      url: '/gallery',
      icon: Image,
      show: true,
    },
    {
      title: 'Account',
      url: '/settings',
      icon: Settings,
      show: !!user,
    },
    {
      title: 'Subscribe',
      url: '/subscription',
      icon: Crown,
      show: !isHidingMonetizationInStores() && !!user && !isRenewableSubscriber && chats.length > 0,
    },
    {
      title: 'Gems',
      url: '/gems',
      icon: Gem,
      show: isSubscriber,
    },
  ];

  const footerItems = [
    // Discord server - hidden for now
    // {
    //   title: 'Discord server',
    //   icon: MessageSquare,
    //   onClick: () => {},
    // },
    {
      title: 'Contact us',
      icon: Mail,
      onClick: () => setContactOpen(true),
    },
    ...(!isFromAppStores() ? [{
      title: 'Affiliate',
      icon: Trophy,
      onClick: () => setAffiliateOpen(true),
    }] : []),
  ];

  return (
    <>
      <Sidebar collapsible="icon" className="bg-zinc-900 border-zinc-800">
        <SidebarHeader className={`border-none border-zinc-800 bg-zinc-900 h-[${EH_HEADER_HEIGHT}px] justify-center md:items-center`}>
          <SidebarTrigger className="text-white bg-zinc-800/80 backdrop-blur-sm hover:bg-zinc-700 p-2 rounded-lg" />
          {!user && (
            <Button
              size="lg"
              className="md:hidden border border-amber-500 text-amber-500 bg-transparent px-2 sm:px-4 py-2 rounded-lg font-semibold hover:bg-amber-950 transition"
              onClick={() => setShowAuthDialog(true)}
            >
              {isPastUser && <span>Login</span>}
              {!isPastUser && (
                <span>Create Free Account</span>
              )}
            </Button>
          )}
          {/* <div className="flex items-center px-2 py-2">
            <Link to="/" className="flex items-center">
              <span className="text-xl font-bold text-white">
                <span>Evening</span><span className="text-amber-500">Honey</span>
              </span>
            </Link>
          </div> */}
        </SidebarHeader>
        
        <SidebarContent className="bg-zinc-900">
          <SidebarGroup>
            {/* <SidebarGroupLabel className="text-zinc-400 text-xs uppercase tracking-wider">Navigation</SidebarGroupLabel> */}
            <SidebarGroupContent>
              <SidebarMenu>
                {contentItems
                  .filter(item => item.show)
                  .map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild
                        size="lg"
                        tooltip={item.title}
                        isActive={location.pathname === item.url || (item.url === '/c' && location.pathname.startsWith('/c/'))}
                        className="text-white hover:bg-zinc-800 data-[active=true]:bg-zinc-800 data-[active=true]:text-amber-500"
                      >
                        <Link to={item.url}>
                          <item.icon className={cn(
                            item.url === '/subscription' && 'text-amber-500',
                            item.url === '/gems' && 'text-blue-400',
                          )} />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="bg-zinc-900 border-t border-zinc-800">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton 
                asChild
                size="lg"
                tooltip="Discord"
                className="text-white hover:bg-zinc-800"
              >
                <Link to="https://discord.gg/vbn9pjFJmj" target="_blank">
                  <img src="/logos/discord-logo-white.svg" alt="Discord server" className="size-5 mr-1" />
                  <span>Discord server</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {footerItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton 
                  onClick={item.onClick}
                  size="lg"
                  tooltip={item.title}
                  className="text-white hover:bg-zinc-800"
                >
                  <item.icon />
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Auth Dialog */}
      <EhAuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
      />

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Contact Us</DialogTitle>
            <DialogDescription className="text-md text-zinc-400">
              <span>Get in touch with our support team.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
              <Mail className="size-5 text-amber-500" />
              <div>
                <div className="font-semibold">Email Support</div>
                <div className="text-sm text-zinc-400">support@eveninghoney.ai</div>
              </div>
            </div>
            <Button 
              asChild
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border-zinc-600"
              variant="outline"
            >
              <a 
                href="mailto:support@eveninghoney.ai" 
                className="flex items-center gap-2"
              >
                <Mail className="size-4" />
                <span>Send Email</span>
                <ExternalLink className="size-4" />
              </a>
            </Button>
          </div>
          <div className="text-center text-zinc-700 text-sm">
            <span>{`User-Agent: ${window.navigator.userAgent}`}</span>
            <br />
            <span>{`${isTwaSession() ? 'TWA' : ''} ${isFromAppStores() ? 'Installed' : ''}`}</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Affiliate Dialog */}
      <Dialog open={affiliateOpen} onOpenChange={setAffiliateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Affiliate Program</DialogTitle>
            <DialogDescription className="text-md text-zinc-400">
              <span>Earn up to 40% commission on recurring subscriptions and credit purchases.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-3 mb-3">
                <DollarSign className="size-6 text-green-500" />
                <div>
                  <div className="font-semibold text-lg">Up to 40% Commission</div>
                  <div className="text-sm text-zinc-400">1-year, recurring</div>
                </div>
              </div>
              <ul className="text-sm text-zinc-300 space-y-1">
                <li>• Starting at 30% commission</li>
                <li>• Monthly payouts</li>
                <li>• Real-time tracking dashboard</li>
                <li>• Promo codes available</li>
              </ul>
            </div>
            <Button 
              asChild
              className="w-full bg-amber-600 hover:bg-amber-500"
            >
              <a 
                href="https://eveninghoney.endorsely.com/" 
                className="flex items-center gap-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="size-4" />
                <span>Apply Now</span>
              </a>
            </Button>
            <div className="flex items-center mb-4 select-none">
              <div className="flex-grow border-t border-zinc-700" />
              <span className="mx-4 text-zinc-400 font-semibold">OR</span>
              <div className="flex-grow border-t border-zinc-700" />
            </div>
            <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
              <Mail className="size-5 text-amber-500" />
              <div>
                <div className="font-semibold">Contact us</div>
                <div className="text-sm text-zinc-400">affiliate@eveninghoney.ai</div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
} 